import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, Ring, Bar, Chip, Confetti, difficulty } from "@/v2/components/ui";
import { fileUrl, prettySize } from "@/v2/lib/files";
import SmartVideo from "@/v2/components/SmartVideo";
import { Q_TYPE_AR } from "@/v2/lib/rbac";
import {
  ArrowRight, Clock, Star, Target, CheckCircle2, XCircle, Loader2, ChevronLeft, ChevronRight,
  Sparkles, BrainCircuit, Lightbulb, Trophy, RotateCcw, PauseCircle, Paperclip, Download, Link2,
  FileText, ArrowUp, ArrowDown, Timer, RefreshCw,
  MousePointerClick, Shuffle, Code2,
} from "lucide-react";

const PHASES = ["جارٍ تحليل الإجابة...", "جارٍ مقارنة الإجابة بمعايير التقييم...", "جارٍ إعداد الملاحظات التعليمية..."];

const seededOrder = (n, seed) => {
  const arr = Array.from({ length: n }, (_, i) => i);
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) & 0xffff;
  for (let i = arr.length - 1; i > 0; i--) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const j = s % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export default function ChallengeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const qc = useQueryClient();
  const startedAt = useRef(Date.now());
  const reported = useRef(0);

  const [watched, setWatched] = useState(0);
  const [progress, setProgress] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState(0);
  const [result, setResult] = useState(null);
  const [notes, setNotes] = useState("");
  const [left, setLeft] = useState(null);

  const { data: ch, isLoading, error } = useQuery({
    queryKey: ["challenge", id, user?.id],
    queryFn: async () => (await api.get(`/challenges/${id}`)).data,
    enabled: Boolean(id && user?.id),
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => { if (id) setNotes(localStorage.getItem(`v2notes-${id}`) || ""); }, [id]);
  useEffect(() => { if (id) localStorage.setItem(`v2notes-${id}`, notes); }, [notes, id]);
  useEffect(() => {
    if (ch?.watch_pct) {
      setWatched((w) => Math.max(w, ch.watch_pct));
      reported.current = Math.max(reported.current, ch.watch_pct);
    }
  }, [ch?.watch_pct]);
  useEffect(() => setVideoError(false), [ch?.video]);

  const limit = Number(ch?.time_limit_minutes || 0);
  useEffect(() => {
    if (!limit || result) return;
    setLeft(limit * 60);
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [limit, result]);

  const questions = ch?.questions || [];
  const orderMaps = useMemo(() => {
    const m = {};
    questions.filter((q) => q.type === "ordering").forEach((q) => { m[q.id] = seededOrder((q.options || []).length, q.id); });
    return m;
  }, [ch?.id, questions.length]);
  const matchOrderMaps = useMemo(() => {
    const m = {};
    questions.filter((q) => q.type === "matching").forEach((q) => {
      m[q.id] = seededOrder((q.config?.rights || []).length, `${q.id}-matching`);
    });
    return m;
  }, [ch?.id, questions.length]);

  if (isLoading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#91cab8]" size={40} /></div>;
  if (error) return <GlassCard className="p-10 text-center text-[#bcc8cf]">{error.response?.data?.detail || "تعذّر تحميل التحدي."}</GlassCard>;

  const videoBlocked = !!ch.video_blocked;
  const requiredPct = ch.video_required_pct ?? 80;
  const hasVideo = !!ch.video && !videoBlocked;
  const videoOk = !hasVideo || requiredPct === 0 || watched >= requiredPct;
  const files = ch.files || [];
  const attemptsUsed = ch.attempts_used || 0;
  const maxAttempts = Number(ch.max_attempts || 0);
  const attemptsLeft = maxAttempts > 0 ? Math.max(0, maxAttempts - attemptsUsed) : null;
  const quizDisabled = ch.quiz_enabled === false;
  const outOfAttempts = attemptsLeft === 0 && ch.status !== "completed";

  const isAnswered = (q) => {
    const v = answers[q.id];
    if (q.type === "multi") return Array.isArray(v) && v.length > 0;
    if (q.type === "ordering") return true;
    if (q.type === "matching") return Array.isArray(v) && v.length === (q.config?.lefts || []).length && v.every((x) => x !== "");
    if (q.type === "hotspot") return v && Number.isFinite(Number(v.x)) && Number.isFinite(Number(v.y));
    return v !== undefined && String(v).trim() !== "";
  };
  const answered = questions.filter(isAnswered).length;
  const allAnswered = questions.length > 0 && answered === questions.length;
  const canSubmit = videoOk && allAnswered && !quizDisabled && !outOfAttempts;
  const diff = difficulty(ch.points);
  const keyPoints = (ch.content || "").split(/[.،]/).map((s) => s.trim()).filter((s) => s.length > 8).slice(0, 3);

  const onVideoProgress = (p) => {
    setProgress(p);
    setWatched((w) => Math.max(w, p));
    if (p - reported.current >= 10) {
      reported.current = p;
      api.post(`/challenges/${id}/progress`, { pct: p }).catch(() => {});
    }
  };
  const setAns = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const orderingValue = (q) => answers[q.id] || (orderMaps[q.id] || []).map(String);
  const moveOrder = (q, i, dir) => {
    const cur = [...orderingValue(q)];
    const t = i + dir;
    if (t < 0 || t >= cur.length) return;
    [cur[i], cur[t]] = [cur[t], cur[i]];
    setAns(q.id, cur);
  };
  const matchingValue = (q) => answers[q.id] || Array((q.config?.lefts || []).length).fill("");
  const setMatch = (q, index, value) => {
    const current = [...matchingValue(q)];
    current[index] = value;
    setAns(q.id, current);
  };
  const choosePoint = (q, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAns(q.id, {
      x: Math.round(((event.clientX - rect.left) / rect.width) * 1000) / 10,
      y: Math.round(((event.clientY - rect.top) / rect.height) * 1000) / 10,
    });
  };

  const submit = async () => {
    setSubmitting(true); setPhase(0); setResult(null);
    const timer = setInterval(() => setPhase((p) => (p + 1) % PHASES.length), 1300);
    const payload = { ...answers };
    questions.filter((q) => q.type === "ordering").forEach((q) => { payload[q.id] = orderingValue(q); });
    try {
      if (hasVideo && watched > 0) await api.post(`/challenges/${id}/progress`, { pct: watched }).catch(() => {});
      const { data } = await api.post(`/challenges/${id}/submit`, {
        answers: payload,
        video_watched: videoOk,
        duration_seconds: Math.round((Date.now() - startedAt.current) / 1000),
      });
      setResult(data);
      await refresh();
      qc.invalidateQueries({ queryKey: ["challenges"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      qc.invalidateQueries({ queryKey: ["challenge", id] });
      if (data.passed) toast.success("أحسنت، لقد اجتزت التحدي بنجاح!"); else toast.warning("لم تجتز التحدي بعد، حاول مرة أخرى.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "حدث خطأ أثناء الإرسال.");
    } finally { clearInterval(timer); setSubmitting(false); }
  };

  const retry = () => { setResult(null); setAnswers({}); setStep(0); startedAt.current = Date.now(); };
  const q = questions[step];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate("/journey")} className="flex items-center gap-2 text-[#bcc8cf] hover:text-white font-semibold"><ArrowRight size={18} /> رجوع إلى الرحلة</button>

      {/* header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Chip tone="gold">اليوم {ch.day}</Chip>
          <Chip tone={diff.tone}>{diff.label}</Chip>
          <Chip tone="emerald"><Star size={12} /> {ch.points} نقطة</Chip>
          <Chip tone="muted"><Clock size={12} /> {ch.video_duration} + اختبار قصير</Chip>
          {attemptsLeft !== null && <Chip tone={attemptsLeft > 0 ? "gold" : "red"}><RefreshCw size={12} /> محاولات متبقية: {attemptsLeft}</Chip>}
          {limit > 0 && left !== null && !result && <Chip tone={left < 60 ? "red" : "muted"}><Timer size={12} /> {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</Chip>}
        </div>
        <h1 className="text-3xl font-extrabold">{ch.title}</h1>
        <p className="text-[#bcc8cf] mt-2">{ch.description}</p>
      </div>

      {/* video */}
      <GlassCard className="p-4">
        {videoBlocked ? (
          <div className="rounded-2xl bg-white/5 border border-white/10 py-14 text-center" data-testid="v2-video-blocked">
            <PauseCircle size={44} className="mx-auto mb-3 text-[#e3c799]" />
            <div className="font-bold text-lg">هذا المحتوى متوقف مؤقتًا من الإدارة.</div>
            <p className="text-sm text-[#bcc8cf] mt-1">يمكنك متابعة بقية أجزاء التحدي، وسيعود الفيديو عند إعادة تشغيله من الإدارة.</p>
          </div>
        ) : !ch.video ? (
          <div className="rounded-2xl bg-white/5 border border-white/10 py-10 text-center text-[#bcc8cf]" data-testid="v2-video-empty">
            لا يتطلب هذا التحدي مشاهدة فيديو. يمكنك الانتقال مباشرة إلى المحتوى والاختبار.
          </div>
        ) : (
          <>
            <SmartVideo
              src={ch.video}
              playlist={ch.video_playlist || []}
              chapters={ch.video_chapters || []}
              title={ch.video_title || ch.title}
              onProgress={onVideoProgress}
              onError={() => setVideoError(true)}
              testId="v2-video"
            />
            <div className="mt-3">
              <div className="flex justify-between text-sm text-[#bcc8cf] mb-1"><span>نسبة مشاهدة الفيديو</span><span>{videoError ? "غير متاح" : `${Math.max(progress, watched)}%`}</span></div>
              <Bar value={videoError ? 0 : Math.max(progress, watched)} />
              {videoError ? <p className="text-xs text-[#F7A08A] mt-2">تعذّر تحميل الفيديو من الرابط الحالي — تواصل مع الإدارة لتحديث الرابط حتى يُفتح الاختبار.</p>
                : watched < requiredPct ? <p className="text-xs text-[#e3c799] mt-2">شاهد {requiredPct}% من الفيديو على الأقل لفتح الاختبار.</p> : null}
            </div>
          </>
        )}
      </GlassCard>

      {/* attachments */}
      {files.length > 0 && (
        <GlassCard className="p-5" data-testid="v2-files">
          <h3 className="font-bold mb-3 flex items-center gap-2"><Paperclip size={16} className="text-[#e3c799]" /> ملفات ومرفقات التحدي</h3>
          <div className="space-y-2">
            {files.map((f, i) => (
              <div key={f.id || i} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                <div className="w-9 h-9 rounded-xl bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center shrink-0">
                  {f.kind === "link" ? <Link2 size={16} /> : <FileText size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{f.title}</div>
                  {f.size ? <div className="text-[11px] text-[#bcc8cf]">{prettySize(f.size)}</div> : null}
                </div>
                <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" download={f.downloadable ? "" : undefined}
                  data-testid={`v2-file-${i}`} className="v2-btn-ghost v2-btn-sm">
                  {f.downloadable ? <><Download size={14} /> تحميل</> : <>عرض</>}
                </a>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* smart summary + objectives + notes */}
      <div className="grid md:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <h3 className="font-bold mb-2 flex items-center gap-2"><Sparkles size={16} className="text-[#e3c799]" /> ملخص ذكي</h3>
          <p className="text-sm text-[#bcc8cf] leading-relaxed mb-4">{ch.content}</p>
          {keyPoints.length > 0 && <>
            <h4 className="font-bold text-sm mb-2 text-[#91cab8]">أهم النقاط المستفادة</h4>
            <ul className="space-y-1.5 text-sm text-[#bcc8cf]">{keyPoints.map((k, i) => <li key={i} className="flex gap-2"><CheckCircle2 size={16} className="text-[#91cab8] shrink-0 mt-0.5" /> {k}</li>)}</ul>
          </>}
        </GlassCard>
        <div className="space-y-4">
          <GlassCard className="p-5">
            <h3 className="font-bold mb-2 flex items-center gap-2"><Target size={16} className="text-[#91cab8]" /> أهداف التحدي</h3>
            <ul className="space-y-1.5 text-sm text-[#bcc8cf]">
              {(ch.objectives || []).length > 0 ? ch.objectives.map((o, i) => (
                <li key={i} className="flex gap-2"><span className="text-[#e3c799]">◆</span> {o}</li>
              )) : (
                <>
                  <li className="flex gap-2"><span className="text-[#e3c799]">◆</span> فهم أساسيات «{ch.title}»</li>
                  <li className="flex gap-2"><span className="text-[#e3c799]">◆</span> تطبيق ما تعلمته بشكل عملي</li>
                  <li className="flex gap-2"><span className="text-[#e3c799]">◆</span> اجتياز اختبار التحدي بنجاح</li>
                </>
              )}
            </ul>
          </GlassCard>
          <GlassCard className="p-5">
            <h3 className="font-bold mb-2">ملاحظات شخصية</h3>
            <textarea data-testid="v2-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="دوّن ملاحظاتك أثناء المشاهدة..." className="w-full rounded-xl bg-white/5 border border-white/10 p-3 text-sm outline-none focus:border-[#bb9661]/40 text-white resize-none" />
          </GlassCard>
        </div>
      </div>

      {/* quiz */}
      {quizDisabled ? (
        <GlassCard className="p-8 text-center" data-testid="v2-quiz-disabled">
          <PauseCircle size={38} className="mx-auto mb-3 text-[#e3c799]" />
          <div className="font-bold">الاختبار متوقف مؤقتًا من الإدارة.</div>
        </GlassCard>
      ) : outOfAttempts && !result ? (
        <GlassCard className="p-8 text-center" data-testid="v2-no-attempts">
          <XCircle size={38} className="mx-auto mb-3 text-[#F7A08A]" />
          <div className="font-bold">استنفدت عدد المحاولات المسموح ({maxAttempts}).</div>
          <p className="text-sm text-[#bcc8cf] mt-1">تواصل مع الإدارة إذا كنت بحاجة إلى محاولة إضافية.</p>
        </GlassCard>
      ) : !result && q ? (
        <GlassCard gold className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">اختبار التحدي</h3>
            <span className="text-sm text-[#bcc8cf]">سؤال {step + 1} من {questions.length}</span>
          </div>
          <Bar value={((step + 1) / questions.length) * 100} className="mb-6" />

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.28 }}>
              <div className="mb-2"><Chip tone="muted">{Q_TYPE_AR[q.type] || "سؤال"}</Chip></div>
              <div className="text-lg font-semibold mb-4">{q.text}</div>

              {(q.type === "mcq" || q.type === "truefalse") ? (
                <div className="space-y-2" data-testid={`v2-question-${step}`}>
                  {q.options.map((opt, oi) => (
                    <label key={oi} data-testid={`v2-opt-${step}-${oi}`} className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${String(answers[q.id]) === String(oi) ? "border-[#bb9661]/60 bg-[#bb9661]/10" : "border-white/10 hover:bg-white/5"}`}>
                      <input type="radio" name={q.id} checked={String(answers[q.id]) === String(oi)} onChange={() => setAns(q.id, String(oi))} className="w-4 h-4 accent-[#91cab8]" />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              ) : q.type === "multi" ? (
                <div className="space-y-2" data-testid={`v2-question-${step}`}>
                  <p className="text-xs text-[#bcc8cf] mb-1">اختر جميع الإجابات الصحيحة.</p>
                  {q.options.map((opt, oi) => {
                    const sel = (answers[q.id] || []).includes(String(oi));
                    return (
                      <label key={oi} data-testid={`v2-opt-${step}-${oi}`} className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${sel ? "border-[#bb9661]/60 bg-[#bb9661]/10" : "border-white/10 hover:bg-white/5"}`}>
                        <input type="checkbox" checked={sel} onChange={() => {
                          const cur = answers[q.id] || [];
                          setAns(q.id, sel ? cur.filter((x) => x !== String(oi)) : [...cur, String(oi)]);
                        }} className="w-4 h-4 accent-[#91cab8]" />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              ) : q.type === "ordering" ? (
                <div className="space-y-2" data-testid={`v2-question-${step}`}>
                  <p className="text-xs text-[#bcc8cf] mb-1">رتّب العناصر بالترتيب الصحيح باستخدام الأسهم.</p>
                  {orderingValue(q).map((oi, i) => (
                    <div key={`${oi}-${i}`} data-testid={`v2-order-${step}-${i}`} className="flex items-center gap-3 p-3 rounded-2xl border border-white/10 bg-white/5">
                      <span className="w-7 h-7 rounded-full bg-[#bb9661]/15 text-[#e3c799] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="flex-1">{q.options[Number(oi)]}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveOrder(q, i, -1)} data-testid={`v2-order-up-${step}-${i}`} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"><ArrowUp size={14} /></button>
                        <button onClick={() => moveOrder(q, i, 1)} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"><ArrowDown size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : q.type === "matching" ? (
                <div className="space-y-3" data-testid={`v2-question-${step}`}>
                  <p className="text-xs text-[#bcc8cf] flex items-center gap-2"><Shuffle size={14} /> صِل كل عنصر بالمفهوم أو النتيجة المناسبة.</p>
                  {(q.config?.lefts || []).map((left, i) => (
                    <div key={i} className="grid sm:grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                      <div className="font-semibold text-sm">{left}</div>
                      <span className="hidden sm:block text-[#e3c799]">←</span>
                      <select value={matchingValue(q)[i]} onChange={(e) => setMatch(q, i, e.target.value)} data-testid={`v2-match-${step}-${i}`} className="v2-input">
                        <option value="">اختر المطابقة...</option>
                        {(matchOrderMaps[q.id] || []).map((rightIndex) => (
                          <option key={rightIndex} value={String(rightIndex)} disabled={matchingValue(q).some((value, row) => row !== i && value === String(rightIndex))}>{q.config.rights[rightIndex]}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              ) : q.type === "hotspot" ? (
                <div className="space-y-3" data-testid={`v2-question-${step}`}>
                  <p className="text-xs text-[#bcc8cf] flex items-center gap-2"><MousePointerClick size={14} /> اضغط مباشرة على مكان الخطأ في الصورة.</p>
                  <button type="button" onClick={(event) => choosePoint(q, event)} className="relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-black/30 cursor-crosshair">
                    <img src={fileUrl(q.config?.image_url)} alt={q.config?.image_alt || q.text} className="block w-full h-auto" />
                    {answers[q.id] && <span className="absolute w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#e3c799] bg-[#bb9661]/30 shadow-[0_0_24px_rgba(187,150,97,.7)] pointer-events-none" style={{ left: `${answers[q.id].x}%`, top: `${answers[q.id].y}%` }} />}
                  </button>
                  <p className="text-xs text-center text-[#bcc8cf]">{answers[q.id] ? "تم تحديد إجابتك — يمكنك الضغط مرة أخرى لتغييرها." : "لم تحدد موضعًا بعد."}</p>
                </div>
              ) : q.type === "formula_fix" ? (
                <div className="space-y-3" data-testid={`v2-question-${step}`}>
                  <div dir="ltr" className="rounded-2xl border border-white/10 bg-[#17252f] p-4 font-mono text-sm text-[#F7A08A] whitespace-pre-wrap">
                    <div className="flex items-center gap-2 text-[#bcc8cf] mb-3"><Code2 size={15} /> الصيغة الحالية</div>
                    {q.config?.starter || "—"}
                  </div>
                  <input dir="ltr" data-testid={`v2-answer-${step}`} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} placeholder="اكتب الصيغة المصححة هنا..." className="w-full rounded-2xl bg-white/5 border border-white/10 p-4 outline-none focus:border-[#bb9661]/40 text-white font-mono text-left" />
                </div>
              ) : (
                <div data-testid={`v2-question-${step}`}>
                <textarea data-testid={`v2-answer-${step}`} rows={q.type === "task" ? 5 : 4} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} placeholder="اكتب إجابتك بالتفصيل..." className="w-full rounded-2xl bg-white/5 border border-white/10 p-4 outline-none focus:border-[#bb9661]/40 text-white resize-none" />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-6">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="v2-prev" className="v2-btn-ghost disabled:opacity-40"><ChevronRight size={18} /> السابق</button>
            {step < questions.length - 1 ? (
              <button onClick={() => setStep((s) => Math.min(questions.length - 1, s + 1))} data-testid="v2-next" className="v2-btn">التالي <ChevronLeft size={18} /></button>
            ) : (
              <button onClick={submit} disabled={!canSubmit} data-testid="v2-submit" className="v2-btn">إرسال الإجابات <Sparkles size={18} /></button>
            )}
          </div>
          {!canSubmit && step === questions.length - 1 && <p className="text-xs text-[#bcc8cf] text-center mt-3">أجب عن جميع الأسئلة{!videoOk ? ` وشاهد ${requiredPct}% من الفيديو` : ""} لتفعيل الإرسال.</p>}
        </GlassCard>
      ) : null}

      {/* AI analysis overlay */}
      <AnimatePresence>
        {submitting && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[#25343f]/80 backdrop-blur-md">
            <div className="text-center px-6">
              <div className="relative w-28 h-28 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-2 border-[#91cab8]/30 v2-orbit" />
                <div className="absolute inset-3 rounded-full border-2 border-t-[#e3c799] border-white/10 animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center"><BrainCircuit size={40} className="text-[#91cab8] v2-float" /></div>
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={phase} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="text-xl font-bold v2-gold-text">{PHASES[phase]}</motion.div>
              </AnimatePresence>
              <p className="text-[#bcc8cf] mt-2 text-sm">نُقيّم إجاباتك بعناية لنمنحك ملاحظات دقيقة</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* result report */}
      {result && (
        <GlassCard gold className="relative p-6 sm:p-8 overflow-hidden" data-testid="v2-result">
          {result.passed && <Confetti />}
          <div className="relative z-10 grid md:grid-cols-[220px_1fr] gap-8 items-center">
            <div className="text-center">
              <Ring value={result.show_score === false ? 100 : result.score} size={180} stroke={14} id="scorering" fail={!result.passed}>
                {result.show_score === false ? (
                  <div className="text-lg font-extrabold v2-gold-text px-4">تم التسليم</div>
                ) : (
                  <>
                    <div className="text-4xl font-extrabold v2-gold-text">{result.score}</div>
                    <div className="text-xs text-[#bcc8cf]">من 100</div>
                  </>
                )}
              </Ring>
              <div className={`mt-3 inline-flex items-center gap-2 font-bold ${result.passed ? "text-[#91cab8]" : "text-[#F7A08A]"}`}>
                {result.passed ? <CheckCircle2 size={20} /> : <XCircle size={20} />} {result.passed ? "ناجح" : "أعد المحاولة"}
              </div>
            </div>
            <div>
              <h3 className={`text-2xl font-extrabold mb-1 ${result.passed ? "text-[#91cab8]" : "text-[#F7A08A]"}`}>{result.passed ? "أحسنت، لقد اجتزت التحدي بنجاح" : "لم تجتز التحدي بعد"}</h3>
              <p className="text-[#bcc8cf] mb-4 text-sm">النجاح من {result.pass_score}% — {result.points_awarded > 0 ? `حصلت على ${result.points_awarded} نقطة!` : result.already_completed ? "أكملت هذا التحدي سابقًا، لن تُمنح النقاط مجددًا." : "لم تُمنح نقاط هذه المرة."}</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["الوضوح", result.clarity], ["الفهم", result.understanding], ["التطبيق العملي", result.application]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl bg-white/5 border border-white/10 p-3 text-center">
                    <div className="text-lg font-extrabold v2-emerald-text">{v}%</div>
                    <div className="text-[11px] text-[#bcc8cf]">{l}</div>
                  </div>
                ))}
              </div>
              {result.ai_mode === "real" && <Chip tone="emerald"><Sparkles size={12} /> تقييم بالذكاء الاصطناعي</Chip>}
            </div>
          </div>

          <div className="relative z-10 grid sm:grid-cols-2 gap-4 mt-4">
            <div className="rounded-2xl bg-[#91cab8]/10 border border-[#91cab8]/25 p-4">
              <div className="flex items-center gap-2 font-bold text-[#91cab8] mb-2"><Sparkles size={16} /> نقاط القوة</div>
              <p className="text-sm text-[#f6f8fa]/80 leading-relaxed">{result.strengths}</p>
            </div>
            <div className="rounded-2xl bg-[#B45309]/12 border border-[#B45309]/30 p-4">
              <div className="flex items-center gap-2 font-bold text-[#F7A08A] mb-2"><Lightbulb size={16} /> جوانب للتحسين</div>
              <p className="text-sm text-[#f6f8fa]/80 leading-relaxed">{result.improvements}</p>
            </div>
          </div>
          <div className="relative z-10 rounded-2xl bg-white/5 border border-white/10 p-4 mt-4">
            <div className="font-bold mb-1 v2-gold-text">ملاحظة تعليمية وتوصية</div>
            <p className="text-sm text-[#bcc8cf]">{result.note} {result.recommendation}</p>
          </div>

          {result.correct_answers && (
            <div className="relative z-10 rounded-2xl bg-white/5 border border-white/10 p-4 mt-4" data-testid="v2-correct-answers">
              <div className="font-bold mb-2 v2-gold-text">الإجابات الصحيحة</div>
              <div className="space-y-2">
                {result.correct_answers.map((c, i) => {
                  const qq = questions.find((x) => x.id === c.id);
                  const txt = Array.isArray(c.correct)
                    ? c.correct.map((v) => (c.options || qq?.options || [])[Number(v)] ?? v).join("، ")
                    : ((c.options || qq?.options || [])[Number(c.correct)] ?? String(c.correct ?? "—"));
                  return (
                    <div key={i} className="text-sm">
                      <div className="text-[#f6f8fa]/85">{qq?.text}</div>
                      <div className="text-[#91cab8]">الصحيح: {txt}</div>
                      {c.explanation && <div className="text-xs text-[#bcc8cf]">{c.explanation}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="relative z-10 mt-6 flex gap-3">
            {result.passed ? (
              <button onClick={() => navigate("/journey")} className="v2-btn flex-1"><Trophy size={18} /> متابعة الرحلة</button>
            ) : (
              <button onClick={retry} data-testid="v2-retry" className="v2-btn-ghost flex-1"><RotateCcw size={18} /> إعادة المحاولة</button>
            )}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
